import type { HaskQuestion } from '@/hooks/useHask';

/**
 * Obbligatorietà condizionale di "Settore LS" e "Settore ITC" in funzione
 * dell'azienda selezionata nel campo "Azienda LSG" del questionario (LME-54).
 *
 * Regola:
 *  - Azienda "Laminazione Sottile" selezionata  -> "Settore LS" obbligatorio
 *  - Azienda "Italcoat" selezionata             -> "Settore ITC" obbligatorio
 *  - Entrambe selezionate                       -> entrambi obbligatori
 *  - Nessuna delle due                          -> nessun obbligo aggiuntivo
 *
 * "Azienda LSG" è una domanda MULTISELECT: possono essere selezionate più
 * aziende contemporaneamente, quindi la regola è additiva.
 *
 * Le aziende sono riconosciute tramite una regex applicata sulla DESCRIZIONE
 * delle risposte selezionate (robusta rispetto ai codici interni). Se i codattr
 * reali dovessero differire, aggiornare solo CONFIG_OBBLIGO_SETTORI qui sotto.
 */
export const CONFIG_OBBLIGO_SETTORI = {
  codattrAziendaLSG: 'aziendaLSG',
  regole: [
    { codattrSettore: 'primolivLS', matchAzienda: /laminazione|^ls\b|\blam\b/i },
    { codattrSettore: 'primolivITC', matchAzienda: /italcoat|\bitc\b/i },
  ],
} as const;

/**
 * Legge le DESCRIZIONI (testo) delle opzioni selezionate per la domanda
 * "Azienda LSG", gestendo i tipi SELECT / MULTISELECT. Il valore della risposta
 * è il codice (o più codici separati da virgola per i MULTISELECT): ogni codice
 * viene risolto nella rispettiva descrizione (`risposte[].valore`).
 */
function getDescrizioniAziendeSelezionate(
  questions: HaskQuestion[],
  answers: Record<string, string>,
): string[] {
  const aziendaEl = questions.find(q => q.codattr === CONFIG_OBBLIGO_SETTORI.codattrAziendaLSG);
  if (!aziendaEl) return [];

  const raw = answers[aziendaEl.codattr] || '';
  if (raw === '') return [];

  const codici = raw.split(',').map(c => c.trim()).filter(c => c !== '');
  return codici
    .map(code => aziendaEl.risposte?.find(r => r.codice === code)?.valore || '')
    .filter(desc => desc !== '');
}

/**
 * Calcola, in base alle aziende LSG selezionate, l'insieme dei codattr dei
 * Settori che devono risultare obbligatori. Se la domanda "Azienda LSG" non è
 * presente nel questionario, non viene applicata alcuna regola.
 */
export function getSettoriObbligatori(
  questions: HaskQuestion[],
  answers: Record<string, string>,
): Set<string> {
  const obbligatori = new Set<string>();

  const descrizioni = getDescrizioniAziendeSelezionate(questions, answers);
  if (descrizioni.length === 0) return obbligatori;

  for (const regola of CONFIG_OBBLIGO_SETTORI.regole) {
    const settoreEl = questions.find(q => q.codattr === regola.codattrSettore);
    if (!settoreEl) continue; // il campo Settore non è presente in questo questionario
    if (descrizioni.some(desc => regola.matchAzienda.test(desc))) {
      obbligatori.add(regola.codattrSettore);
    }
  }
  return obbligatori;
}

/**
 * Indica se una specifica domanda è obbligatoria tenendo conto sia
 * dell'obbligatorietà di base (`required === 'S'`) sia della regola
 * condizionale sui Settori.
 */
export function isQuestionRequired(
  question: HaskQuestion,
  questions: HaskQuestion[],
  answers: Record<string, string>,
): boolean {
  if (question.required === 'S') return true;
  return getSettoriObbligatori(questions, answers).has(question.codattr);
}
